import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

/**
 * Blocking modal shown to suppliers who have not yet accepted the current
 * version of the conformity clause (v1.0 at launch, bumps in the future).
 * The modal is not dismissable — the supplier must accept before returning to
 * the app. Backend enforces the same block on POST /orders/{id}/bids and
 * POST /orders/{id}/express-accept (HTTP 409 conformity_clause_required).
 */
export default function ConformityClauseGate() {
  const { user, refresh } = useAuth();
  const [clause, setClause] = useState(null);
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);

  const needsAcceptance =
    user &&
    user.role === "supplier" &&
    user.conformity_clause_version_accepted !== user.conformity_clause_current_version;

  useEffect(() => {
    if (!needsAcceptance) return;
    let mounted = true;
    api.get("/legal/supplier-conformity-clause").then(({ data }) => {
      if (mounted) setClause(data);
    }).catch(() => {});
    return () => { mounted = false; };
  }, [needsAcceptance]);

  const accept = async () => {
    if (!checked) return;
    setBusy(true);
    try {
      await api.post("/legal/accept-conformity-clause");
      toast.success("Clausola accettata — puoi tornare a offrire");
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!needsAcceptance) return null;

  return (
    <Dialog open modal>
      <DialogContent
        className="max-w-2xl"
        // Prevent close on ESC / outside click — this is a mandatory gate.
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        data-testid="conformity-clause-gate"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-emerald-700" />
            {clause?.title || "Clausola di conformità del fornitore"}
          </DialogTitle>
        </DialogHeader>
        <div className="text-xs font-mono uppercase tracking-widest text-slate-500 -mt-2">
          Versione {clause?.version || "…"} · obbligatoria per continuare a offrire
        </div>
        <div className="mt-3 max-h-72 overflow-y-auto rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-800 whitespace-pre-line" data-testid="conformity-clause-text">
          {clause?.text || "Caricamento…"}
        </div>
        <label className="mt-4 flex items-start gap-3 cursor-pointer" data-testid="conformity-clause-checkbox-row">
          <Checkbox
            checked={checked}
            onCheckedChange={(v) => setChecked(!!v)}
            data-testid="conformity-clause-checkbox"
          />
          <span className="text-sm text-slate-700">
            Ho letto e accetto integralmente la clausola di conformità nella versione qui riportata.
          </span>
        </label>
        <div className="mt-5 flex justify-end">
          <Button
            onClick={accept}
            disabled={!checked || busy || !clause}
            className="ga-btn-primary rounded-full"
            data-testid="conformity-clause-accept-button"
          >
            {busy ? "Invio…" : "Accetto e continuo"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
